import { useEffect, useRef, useState } from 'react'
import { Bookmark, BookmarkPlus, Trash2 } from 'lucide-react'
import { useT } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * A3.03 (umumiy) — Saqlangan filtrlar / segmentlar.
 *
 * DataTable holati URL'da yashaydi (X1) — demak «saqlangan filtr» = nomlangan
 * URL-query. Server-endpoint talab qilinmaydi: ro'yxat klient-localStorage'da,
   har ekran uchun alohida kalit. Bu SOXTA server-segment emas — shaxsiy
 * tezkor yorliq; ulashish URL'nining o'zi orqali ishlaydi (havola nusxalansa
 * filtr ham ko'chadi).
 */

interface SavedView {
  name: string
  search: string
}

function load(key: string): SavedView[] {
  try {
    const raw = localStorage.getItem(`grom_admin_views_${key}`)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr)
      ? arr.filter((v): v is SavedView => typeof v?.name === 'string' && typeof v?.search === 'string')
      : []
  } catch {
    return []
  }
}

function save(key: string, views: SavedView[]): void {
  try {
    localStorage.setItem(`grom_admin_views_${key}`, JSON.stringify(views))
  } catch {
    /* localStorage yopiq — saqlash jimgina o'chadi */
  }
}

export function SavedViews({
  storageKey,
  onApply,
}: {
  /** Ekran kaliti, masalan 'users' — har jadvalning o'z ro'yxati. */
  storageKey: string
  /** Tanlangan query (`?`siz) qo'llanadi — odatda jadvalni remount qilish. */
  onApply: (search: string) => void
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [views, setViews] = useState<SavedView[]>(() => load(storageKey))
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (naming) inputRef.current?.focus()
  }, [naming])

  // Tashqariga bosilganda yopiladi.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) {
        setOpen(false)
        setNaming(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const currentQuery = () => window.location.search.replace(/^\?/, '')

  const persist = (next: SavedView[]) => {
    setViews(next)
    save(storageKey, next)
  }

  const addView = () => {
    const n = name.trim()
    if (!n) return
    persist([...views.filter((v) => v.name !== n), { name: n, search: currentQuery() }])
    setName('')
    setNaming(false)
  }

  return (
    <div ref={rootRef} className="relative">
      <Button variant="ghost" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <Bookmark size={13} />
        {t.savedViews.title}
      </Button>

      {open && (
        <div
          className="absolute right-0 z-30 mt-1 w-72 rounded-md border border-border-default bg-surface-2 p-2"
          style={{ boxShadow: 'var(--shadow-panel)' }}
          role="menu"
        >
          {views.length === 0 && !naming && (
            <p className="px-1.5 py-2 text-sm text-tertiary">{t.savedViews.empty}</p>
          )}
          <ul className="max-h-56 space-y-0.5 overflow-auto">
            {views.map((v) => (
              <li key={v.name} className="group flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    onApply(v.search)
                    setOpen(false)
                  }}
                  className="min-w-0 flex-1 truncate rounded-sm px-1.5 py-1.5 text-left text-sm text-primary hover:bg-surface-3"
                >
                  {v.name}
                </button>
                <button
                  type="button"
                  aria-label={t.common.delete}
                  onClick={() => persist(views.filter((x) => x.name !== v.name))}
                  className="rounded-sm p-1 text-tertiary opacity-0 group-hover:opacity-100 hover:text-danger focus:opacity-100"
                >
                  <Trash2 size={13} />
                </button>
              </li>
            ))}
          </ul>

          {naming ? (
            <form
              className="mt-1.5 flex items-center gap-1.5 border-t border-border-subtle pt-1.5"
              onSubmit={(e) => {
                e.preventDefault()
                addView()
              }}
            >
              <Input
                ref={inputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t.savedViews.namePlaceholder}
                maxLength={60}
              />
              <Button type="submit" variant="primary" size="sm" disabled={!name.trim()}>
                {t.common.save}
              </Button>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => setNaming(true)}
              className="mt-1.5 flex w-full items-center gap-1.5 rounded-sm border-t border-border-subtle px-1.5 pt-2 pb-1 text-sm text-accent hover:underline"
            >
              <BookmarkPlus size={13} />
              {t.savedViews.saveCurrent}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
